/** @format */

import { Asset } from 'contentful';
import { forwardRef, useEffect } from 'react';
import { styled } from 'styled-components';
import 'swiper/css';
import 'swiper/css/navigation';
import { Keyboard } from 'swiper/modules';
import { SwiperRef } from 'swiper/react';
import { useMediaQueryIndex } from '../../hooks/useMediaQuery';
import { breakpointUpTo } from '../../theme/mixins';
import { Image } from '../Image';

import { Swiper, SwiperSlide } from 'swiper/react';

export const SliderExternalControls = forwardRef<SwiperRef, Props>(
  ({ images, isFirstSlideState, isLastSlideState, activeSlideState, classNameState }, ref) => {
    const [isFirstSlide, setIsFirstSlide] = isFirstSlideState;
    const [isLastSlide, setIsLastSlide] = isLastSlideState;
    const [activeSlide, setActiveSlide] = activeSlideState;
    const [currentClassName, setCurrentClassName] = classNameState;

    const mediaQueryIndex = useMediaQueryIndex();
    const mobile = !!(mediaQueryIndex !== null && mediaQueryIndex < 2);

    useEffect(() => {
      if (!ref || !('current' in ref)) return;

      ref.current?.swiper.on('slideChange', () => {
        setIsFirstSlide(ref.current?.swiper.isBeginning!);

        setIsLastSlide(ref.current?.swiper.activeIndex === images.length - 1);

        setActiveSlide(ref.current?.swiper.activeIndex! + 1);
      });

      ref.current?.swiper.on('slideNextTransitionStart', () => {
        setCurrentClassName('forward');
      });

      ref.current?.swiper.on('slidePrevTransitionStart', () => {
        setCurrentClassName('backward');
      });
    }, []);

    const labelCounter = `${activeSlide}/${images.length}`;

    return (
      <ContainerSwiper>
        <StyledSwiper
          ref={ref}
          keyboard={{ enabled: true }}
          modules={[Keyboard]}
          allowSlidePrev={!isFirstSlide}
          allowSlideNext={!isLastSlide}
          spaceBetween={16}
          slidesPerView={1}
        >
          {images.map((img, i) => (
            <SwiperSlide className={currentClassName} key={i}>
              <Image image={img} />
            </SwiperSlide>
          ))}
          {mobile && (
            <Counter>
              <h3>{labelCounter}</h3>
            </Counter>
          )}
        </StyledSwiper>
      </ContainerSwiper>
    );
  },
);
type Props = {
  images: Asset[];
  isFirstSlideState: [boolean, React.Dispatch<React.SetStateAction<boolean>>];
  isLastSlideState: [boolean, React.Dispatch<React.SetStateAction<boolean>>];
  activeSlideState: [number, React.Dispatch<React.SetStateAction<number>>];
  classNameState: [string, React.Dispatch<React.SetStateAction<string>>];
};

const Counter = styled.div`
  padding: 0 0.75rem;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  right: 0px;
  bottom: 2rem;
  z-index: 9;
  position: absolute;
  background: ${({ theme }) => theme.color.teal.dark};
  width: 5rem;
  height: 2rem;

  h3 {
    color: ${({ theme }) => theme.color.teal.light};
    font-family: ${({ theme }) => theme.font.title};
    font-weight: 400;
  }
`;

const ContainerSwiper = styled.div`
  max-width: 100%;
  height: 100%;

  .swiper-slide {
    border-radius: 1.56rem;
    aspect-ratio: 1;
    height: 100%;
    border: 1px solid;
    overflow: hidden;
    border: none;

    ${breakpointUpTo.md`
      border-radius: 0;
    `}

    img {
      bottom: 0;
      position: absolute;
      max-width: 100%;
      height: auto;
      object-fit: cover;
      display: block;
      min-block-size: -webkit-fill-available;

      ${breakpointUpTo.md`
        border-radius: 0;
        height: 100%;
      `}
    }
  }
`;
const StyledSwiper = styled(Swiper)`
  padding-left: 10px;
  height: 100%;

  ${breakpointUpTo.md`
    padding-left: 0;
  `}

  .swiper-slide {
    width: 4rem;
  }

  .swiper-wrapper {
    transition: transform 2s ease-in-out;
  }

  .swiper-slide:not(.swiper-slide-active) {
    transition: transform 1s;
  }

  .swiper-slide:first-of-type.forward {
    animation: none !important;
  }

  .swiper-slide:not(.swiper-slide-active).backward {
    animation-delay: 0s;
    animation-direction: reverse;
  }

  .swiper-slide-active.forward {
    animation: bounce 1s;
    animation-delay: 0.3s, 0s;
  }

  .swiper-slide-active.backward {
    animation: bounceBackward 1s;
    animation-delay: 0.3s;
  }

  @keyframes bounce {
    0% {
      transform: translateX(-10px);
    }

    100% {
      transform: translateX(0);
    }
  }

  @keyframes bounceBackward {
    0% {
      transform: translateX(10px);
    }

    100% {
      transform: translateX(0);
    }
  }
`;

SliderExternalControls.displayName = 'SliderExternalControls';
